"use client"

import { useEffect, useMemo, useState, type ReactNode } from "react"

import { Portal, Transition } from "@headlessui/react"
import Image from "next/image"
import { useParams, useSearchParams } from "next/navigation"
import { useTranslations } from "next-intl"

import { ListingBnplBand } from "@/components/marketplace/bnpl/listing-band"
import { categoryVisual } from "@/components/marketplace/category-visual"
import { PhotoTile } from "@/components/marketplace/discovery/photo-tile"
import { buildDiscovery } from "@/components/marketplace/layout/search-discovery/get-discovery"
import type { DiscoverySection } from "@/components/marketplace/layout/search-discovery/types"
import { SearchProductOption } from "@/components/marketplace/layout/search-product-option"
import { FilterPanel } from "@/components/marketplace/listing/filter-panel"
import type { FacetOption } from "@/components/marketplace/listing/types"
import { Price } from "@/components/marketplace/product/price"
import type { NavCategory, Product } from "@/components/marketplace/types"
import {
  ArrowUpRightIcon,
  BackIcon,
  Button,
  ChevronIcon,
  ClockIcon,
  CloseIcon,
  CountBadge,
  FilterIcon,
  FireIcon,
  Grid,
  Heading,
  IconButton,
  Pill,
  Reel,
  Row,
  SearchIcon,
  Text,
} from "@/components/ui"
import { LocalizedClientLink } from "@/components/ui/localized-client-link"
import { Link } from "@/i18n/navigation"
import { useRecentSearches } from "@/lib/hooks/search/use-recent-searches"
import { useSearchData } from "@/lib/hooks/search/use-search-data"
import { useLocalizedPush } from "@/lib/hooks/use-navigation"
import { useOverlayDismiss, useOverlayPreview } from "@/lib/hooks/use-overlay-dismiss"
import { DEFAULT_REGION } from "@/lib/region"
import { buildRoute, localizeHref } from "@/lib/routes"
import { buildSearchOptions, findExactSearchProduct, type SearchOption } from "@/lib/services/search/search-options"
import { DEFAULT_SEARCH_QUERY, SEARCH_PARAM_KEYS, type SearchQuery } from "@/lib/types/search"
import { createConversionId } from "@/platform/observability/analytics/conversions"
import { trackSearch } from "@/platform/observability/analytics/v2/search"

const TRENDING = ["iPhone", "Tenis Adidas", "Perfumes", "Smart TV", "Audífonos"]
// Six left roughly 470px of the 844px screen empty under the grid at 390.
const SUGGESTION_LIMIT = 12

const countActiveFilters = (query: SearchQuery) => query.categories.length + (query.priceMin !== undefined || query.priceMax !== undefined ? 1 : 0) + (query.inStockOnly ? 1 : 0)

const GroupTitle = ({ children, action }: { children: string; action?: ReactNode }) => (
  <Row justify="between" align="center" className="mt-400 mb-200">
    <Text as="span" variant="eyebrow" tone="subtle">
      {children}
    </Text>
    {action}
  </Row>
)

const RecentSection = ({ recent, onRemove, onClear, onClose }: { recent: string[]; onRemove: (query: string) => void; onClear: () => void; onClose?: () => void }) => {
  const t = useTranslations("marketplace.searchOverlay")
  if (recent.length === 0) return null
  return (
    <>
      <GroupTitle
        action={
          <Button variant="quiet" size="sm" onClick={onClear} className="text-fg-muted hover:text-fg h-auto px-100 py-50 font-semibold">
            {t("clear")}
          </Button>
        }
      >
        {t("recent")}
      </GroupTitle>
      <Row wrap gap="150">
        {recent.map((term) => (
          <Text key={term} as="span" variant="captionLg" tone="inherit" className="border-line-subtle bg-surface inline-flex items-center rounded-full border py-50 pr-100 pl-250">
            <LocalizedClientLink href={buildRoute.search({ text: term })} onClick={onClose} className="text-fg inline-flex items-center gap-150 font-medium">
              <ClockIcon size={12} className="text-fg-subtle" />
              {term}
            </LocalizedClientLink>
            <IconButton
              aria-label={t("removeRecent", { query: term })}
              variant="ghost"
              className="text-fg-subtle hover:text-fg ml-100 size-5"
              icon={<CloseIcon size={12} />}
              onClick={() => onRemove(term)}
            />
          </Text>
        ))}
      </Row>
    </>
  )
}

const TrendingSection = ({ items, onClose }: { items: string[]; onClose?: () => void }) => {
  const t = useTranslations("marketplace.searchOverlay")
  return (
    <>
      <GroupTitle>{t("popularNow")}</GroupTitle>
      <div>
        {items.map((term, index) => (
          <LocalizedClientLink
            key={term}
            href={buildRoute.search({ text: term })}
            onClick={onClose}
            className="hover:bg-surface-subtle border-line-subtle -mx-200 flex items-center gap-300 border-b px-200 py-250 last:border-b-0"
          >
            {/* A ranked chart, so the rank is set as a numeral in the display face
                rather than boxed in a pastel disc — the same register the editorial
                bands count in. */}
            <Heading as="span" variant="sm" family="display" tone="faint" numeric className="w-5 shrink-0 text-center font-black">
              {index + 1}
            </Heading>
            <Text as="span" variant="body" tone="default" truncate className="min-w-0 flex-1 font-semibold">
              {term}
            </Text>
            {index < 2 && <FireIcon size={14} className="text-fg-danger shrink-0" />}
            <ChevronIcon size={14} className="text-fg-subtle shrink-0 opacity-40" />
          </LocalizedClientLink>
        ))}
      </div>
    </>
  )
}

const SuggestedSection = ({ items, region, onClose }: { items: Product[]; region: string; onClose?: () => void }) => {
  const t = useTranslations("marketplace.searchOverlay")
  return (
    <>
      <GroupTitle>{t("suggestedProducts")}</GroupTitle>
      <Reel gap="200" className="-mx-400 px-400">
        {items.map((product) => (
          <Link key={product.id} href={product.href ?? localizeHref(buildRoute.search({ text: product.title }), region)} onClick={onClose} className="group w-33 shrink-0">
            <div className="rounded-8x bg-surface-newgray relative aspect-square overflow-hidden">
              <Image src={product.image} alt="" fill sizes="132px" className="duration-moderate ease-productive object-cover transition-transform group-hover:scale-105" />
            </div>
            <Text as="div" variant="captionLg" family="text" tone="default" className="mt-150 line-clamp-2">
              {product.title}
            </Text>
            <Price size="sm" amount={product.price} className="mt-50" />
          </Link>
        ))}
      </Reel>
    </>
  )
}

// Departments as photographs, not pastel glyph chips. category-visual resolves every
// handle to an editorial image precisely so the drawer, the mega, the feed and this
// overlay read the same; the chip version also truncated nine of fourteen labels at
// 390, because a glyph plus a border left ~100px for a name like "Electrónica, Audio
// y Video". On a scrim the name gets the full tile width and two lines.
const BrowseCategoriesSection = ({ items, region, onClose }: { items: { label: string; handle: string; photo?: string }[]; region: string; onClose?: () => void }) => {
  const t = useTranslations("marketplace.searchOverlay")
  return (
    <>
      <GroupTitle>{t("browseCategories")}</GroupTitle>
      <Grid cols={2} gap="200">
        {items.map((category) => {
          const { palette, image } = categoryVisual(category.handle, category.photo)
          return (
            <PhotoTile
              key={category.handle}
              image={image}
              title={category.label}
              href={localizeHref(buildRoute.category(category.handle), region)}
              palette={palette}
              aspect="aspect-banner"
              size="sm"
              sizes="50vw"
              arrow={false}
              onClick={onClose}
            />
          )
        })}
      </Grid>
    </>
  )
}

interface DiscoveryBodyProps {
  sections: DiscoverySection[]
  recent: string[]
  onRemoveRecent: (query: string) => void
  onClearRecent: () => void
  region: string
  onClose?: () => void
}

const DiscoveryBody = ({ sections, recent, onRemoveRecent, onClearRecent, region, onClose }: DiscoveryBodyProps) => (
  <div className="px-400 pt-100 pb-600">
    {sections.map((section) => {
      switch (section.type) {
        case "recent":
          return <RecentSection key="recent" recent={recent} onRemove={onRemoveRecent} onClear={onClearRecent} onClose={onClose} />
        case "trending":
          return <TrendingSection key="trending" items={section.items} onClose={onClose} />
        case "suggested":
          return <SuggestedSection key="suggested" items={section.items} region={region} onClose={onClose} />
        case "browseCategories":
          return <BrowseCategoriesSection key="browseCategories" items={section.items} region={region} onClose={onClose} />
        default:
          return null
      }
    })}
    {/* Closes the panel rather than opening it: someone who tapped search came to
        search, and an offer above their own intent reads as an ad. */}
    <ListingBnplBand className="mt-400" />
  </div>
)

const SuggestionResults = ({
  query,
  options,
  loading,
  stale,
  onSubmitQuery,
  onSelectOption,
}: {
  query: string
  options: SearchOption[]
  loading?: boolean
  stale?: boolean
  onSubmitQuery: () => void
  onSelectOption: (option: SearchOption) => void
}) => {
  const t = useTranslations("marketplace.searchOverlay")
  return (
    <div className="px-400 pt-200 pb-600">
      <LocalizedClientLink
        href={buildRoute.search({ text: query })}
        onClick={(event) => {
          event.preventDefault()
          onSubmitQuery()
        }}
        className="rounded-6x border-line-subtle hover:border-line-brand hover:bg-brand-subtle text-body text-fg mb-300 flex items-center gap-300 border px-300 py-250 font-semibold"
      >
        <span className="text-fg-brand">
          <SearchIcon size={18} />
        </span>
        <span className="min-w-0 flex-1 truncate">{t.rich("searchFor", { query, b: (chunks) => <b className="font-bold">{chunks}</b> })}</span>
        <span className="text-fg-subtle">
          <ArrowUpRightIcon size={16} />
        </span>
      </LocalizedClientLink>

      {options.length > 0 ? (
        <div className={`duration-quick transition-opacity ${stale ? "opacity-50" : ""}`}>
          {options.map((option) => (
            <LocalizedClientLink
              key={option.key}
              href={option.kind === "product" ? option.href : buildRoute.search({ text: option.term })}
              onClick={(event) => {
                event.preventDefault()
                onSelectOption(option)
              }}
              className={`text-body-sm text-fg border-line-subtle -mx-200 flex items-center border-b px-200 font-medium last:border-b-0 ${option.kind === "product" ? "py-200" : "min-h-11 gap-300 py-250"}`}
            >
              {option.kind === "product" ? (
                <SearchProductOption product={option.product} />
              ) : (
                <>
                  <SearchIcon size={15} className="text-fg-subtle shrink-0" />
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  <ArrowUpRightIcon size={14} className="text-fg-subtle shrink-0" />
                </>
              )}
            </LocalizedClientLink>
          ))}
        </div>
      ) : (
        <Text as="p" variant="bodySm" tone="muted" className="px-100 py-300">
          {loading ? t("searching") : t("noSuggestions", { query })}
        </Text>
      )}
    </div>
  )
}

interface SearchOverlayProps {
  open: boolean
  onClose?: () => void
  defaultMode?: "list" | "filter"
  trending?: string[]
  suggested?: Product[]
  categories?: NavCategory[]
  categoryOptions?: FacetOption[]
}

export const SearchOverlay = ({ open, onClose, defaultMode = "list", trending = TRENDING, suggested = [], categories = [], categoryOptions }: SearchOverlayProps) => {
  const isPreview = useOverlayPreview()
  const t = useTranslations("marketplace.searchOverlay")
  const params = useParams<{ countryCode?: string }>()
  const region = params.countryCode ?? DEFAULT_REGION
  const localizedPush = useLocalizedPush()
  const { recent, add: addRecent, remove: removeRecent, clear: clearRecent } = useRecentSearches()
  const discovery = useMemo(() => buildDiscovery({ trending, suggested, categories }), [trending, suggested, categories])

  const [mode, setMode] = useState(defaultMode)
  const [query, setQuery] = useState("")
  const [draft, setDraft] = useState<SearchQuery>(DEFAULT_SEARCH_QUERY)
  const searchParams = useSearchParams()
  // Opening on the results page picks up the active query so refining doesn't start blank.
  useEffect(() => {
    if (open) setQuery(searchParams.get(SEARCH_PARAM_KEYS.Q) ?? "")
  }, [open, searchParams])
  const filtering = mode === "filter"
  const activeFilters = countActiveFilters(draft)
  useOverlayDismiss(open, onClose)

  const updateDraft = (updates: Partial<SearchQuery>) => setDraft((current) => ({ ...current, ...updates }))
  const navigate = (term: string, href: string) => {
    if (term.length > 0) {
      trackSearch({ search_term: term, results_count: liveData?.totalCount, conversionId: createConversionId() })
      addRecent(term)
    }
    localizedPush(href)
    onClose?.()
  }
  const goToResults = () => {
    const term = query.trim()
    const exactProduct = debouncedQuery === term && !isPlaceholderData ? findExactSearchProduct(liveData?.products ?? [], term) : undefined
    const href = exactProduct?.productUrl ? buildRoute.productFromUrl(exactProduct.productUrl) : buildRoute.search({ ...draft, text: term })
    navigate(term, href)
  }
  const clearFilters = () => setDraft((current) => ({ ...current, categories: [], collections: [], priceMin: undefined, priceMax: undefined, inStockOnly: undefined }))

  const [debouncedQuery, setDebouncedQuery] = useState("")
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 250)
    return () => clearTimeout(id)
  }, [query])
  const searching = !filtering && debouncedQuery.length >= 2
  const { data: liveData, isFetching, isPlaceholderData } = useSearchData({ ...DEFAULT_SEARCH_QUERY, text: debouncedQuery }, 1, { enabled: open && searching })
  const liveOptions = useMemo(
    () => buildSearchOptions({ query: debouncedQuery, suggestions: liveData?.suggestions ?? [], products: liveData?.products ?? [], limit: SUGGESTION_LIMIT }),
    [liveData, debouncedQuery]
  )
  const selectOption = (option: SearchOption) => {
    const term = query.trim()
    if (isPlaceholderData || debouncedQuery !== term) {
      navigate(term, buildRoute.search({ ...draft, text: term }))
      return
    }
    if (option.kind === "query") {
      localizedPush(buildRoute.search({ ...draft, text: option.term }))
      onClose?.()
      return
    }
    navigate(term, option.href)
  }

  return (
    <Portal enabled={!isPreview}>
      <Transition
        appear
        show={open}
        as="div"
        role="dialog"
        aria-modal="true"
        aria-label={t("dialogLabel")}
        className="bg-scrim z-overlay vv-bottom fixed inset-0 flex touch-none flex-col backdrop-blur-md"
        enter="ease-expressive duration-moderate transition motion-reduce:transition-none"
        enterFrom="-translate-y-3 opacity-0"
        enterTo="translate-y-0 opacity-100"
        leave="ease-productive duration-quick transition motion-reduce:transition-none"
        leaveFrom="translate-y-0 opacity-100"
        leaveTo="-translate-y-3 opacity-0"
      >
        <Row className="bg-surface border-line-subtle gap-200 border-b px-250 py-250">
          <IconButton aria-label={t("closeSearch")} variant="ghost" className="text-fg hover:bg-surface-subtle size-10" icon={<BackIcon />} onClick={onClose} />
          <Row className="bg-surface-subtle rounded-6x h-11 min-w-0 flex-1 gap-250 px-350">
            <span className="text-fg-subtle">
              <SearchIcon size={17} />
            </span>
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") goToResults()
              }}
              autoComplete="off"
              spellCheck={false}
              inputMode="search"
              enterKeyHint="search"
              placeholder={t("placeholder")}
              className="text-field text-fg placeholder:text-fg-muted w-full min-w-0 self-stretch border-0 bg-transparent outline-none"
            />
          </Row>
          {/* Glyph only. Spelling out "Filtros" took 84px off a 390 bar and clipped
              the placeholder to "Busca productos, marc"; the field is the reason
              this screen exists, so it gets the width. */}
          <Pill size="sm" tone="outline" selected={filtering} aria-label={t("filters")} onClick={() => setMode(filtering ? "list" : "filter")} className="shrink-0 px-250">
            <FilterIcon size={16} />
            <CountBadge count={activeFilters} className="font-extrabold" />
          </Pill>
        </Row>

        <div className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain p-300">
          <div className="bg-surface rounded-12x shadow-soft overflow-hidden">
            {filtering ? (
              <div className="px-400 pt-200 pb-600">
                <FilterPanel query={draft} categoryOptions={categoryOptions} onUpdateQuery={updateDraft} />
                <div className="bg-surface border-line-subtle pb-safe-offset-300 sticky bottom-0 -mx-400 mt-400 flex gap-200 border-t px-400 py-300">
                  <Button variant="secondary" size="md" fullWidth onClick={clearFilters} disabled={activeFilters === 0}>
                    {t("clear")}
                  </Button>
                  <Button variant="primary" size="md" fullWidth onClick={goToResults}>
                    {t("viewResults")}
                  </Button>
                </div>
              </div>
            ) : searching ? (
              <SuggestionResults
                query={debouncedQuery}
                options={liveOptions}
                loading={isFetching}
                stale={isPlaceholderData}
                onSubmitQuery={goToResults}
                onSelectOption={selectOption}
              />
            ) : (
              <DiscoveryBody sections={discovery.sections} recent={recent} onRemoveRecent={removeRecent} onClearRecent={clearRecent} region={region} onClose={onClose} />
            )}
          </div>
        </div>
      </Transition>
    </Portal>
  )
}
